/**
 * Gives reviewers a visual backstop for incomplete smart-crop rejection labels
 * while preserving the container's asset-strategy keyed state ownership.
 */

import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Image from '@amzn/meridian/image';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { AssetDto } from 'src/store/generated/leela-api-generated';
import { sanitizeMeridianImageSrc } from 'src/utils';
import { assetStrategyKey } from 'src/utils/assetKeys';
import { isCompleteRejectReason } from 'src/utils/rejectReason';
import {
  RejectReasonSelect,
  type RejectReasonSelection,
} from '../RejectReasonSelect';

interface RejectReasonBackstopProps {
  readonly assets: readonly AssetDto[];
  readonly rejectReasonValues?: ReadonlyMap<string, RejectReasonSelection>;
  readonly onRejectReasonChange?: (
    asset: AssetDto,
    value: RejectReasonSelection,
  ) => void;
}

export function getIncompleteRejectAssets (
  assets: readonly AssetDto[],
  rejectReasonValues?: ReadonlyMap<string, RejectReasonSelection>,
): AssetDto[] {
  return assets.filter((asset) =>
    !isCompleteRejectReason(
      rejectReasonValues?.get(assetStrategyKey(asset)),
    ),
  );
}

const BACKSTOP_CARD_WIDTH = 360;

function getAssetTitle (asset: AssetDto): string {
  const title = asset.titles.map(({ name }) => name).filter(Boolean).join(', ');
  return title || asset.assetId;
}

interface RejectReasonBackstopRowProps {
  readonly asset: AssetDto;
  readonly rejectReasonValues?: ReadonlyMap<string, RejectReasonSelection>;
  readonly onRejectReasonChange?: (
    asset: AssetDto,
    value: RejectReasonSelection,
  ) => void;
}

const RejectReasonBackstopRow: React.FC<RejectReasonBackstopRowProps> = ({
  asset,
  rejectReasonValues,
  onRejectReasonChange,
}) => {
  const assetKey = assetStrategyKey(asset);
  const assetTitle = getAssetTitle(asset);
  const rowTestId = `asset-reject-reason-backstop-${assetKey}`;

  return (
    <Box
      data-testid={`${rowTestId}-row`}
      type="outline"
      width={BACKSTOP_CARD_WIDTH}
    >
      <Image
        data-testid={`${rowTestId}-image`}
        size="contain"
        src={sanitizeMeridianImageSrc(asset.assetUrl)}
        viewportAspectRatio={1}
      >
        {assetTitle}
      </Image>
      <Box spacingInset="300">
        <Column spacing="200" width="100%">
          <Text data-testid={`${rowTestId}-title`} type="b300">
            {assetTitle}
          </Text>
          <RejectReasonSelect
            testIdPrefix={`${rowTestId}-reject-reason`}
            value={rejectReasonValues?.get(assetKey) ?? {}}
            onChange={(value) => {
              onRejectReasonChange?.(asset, value);
            }}
          />
        </Column>
      </Box>
    </Box>
  );
};

export const RejectReasonBackstop: React.FC<RejectReasonBackstopProps> = ({
  assets,
  rejectReasonValues,
  onRejectReasonChange,
}) => {
  const { t } = useTranslation();

  if (assets.length === 0) {
    return null;
  }

  return (
    <Column
      data-testid="asset-reject-reason-backstop"
      spacing="300"
      width="100%"
    >
      <Column spacing="100">
        <Text type="h200">{t('asset_reject_reason_backstop_header')}</Text>
        <Text color="secondary">
          {t('asset_reject_reason_backstop_description')}
        </Text>
      </Column>
      <Row
        alignmentVertical="stretch"
        data-testid="asset-reject-reason-backstop-grid"
        spacing="300"
        widths="fit"
        wrap="down"
      >
        {assets.map((asset) => (
          <RejectReasonBackstopRow
            asset={asset}
            key={assetStrategyKey(asset)}
            rejectReasonValues={rejectReasonValues}
            onRejectReasonChange={onRejectReasonChange}
          />
        ))}
      </Row>
    </Column>
  );
};
